import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  FormSectionDto,
  SortSectionQuestionsDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_FORM_SECTIONS_CONFIG } from "../domain";

import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";
import { formsSectionsConfigApi } from "@api/services";

const ACTION_SORT_SECTION_QUESTIONS = "SORT_SECTION_QUESTIONS";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type SortSectionQuestionsProps = {
  sectionId: string;
  dto: SortSectionQuestionsDto;
};

const sortSectionQuestions = async (
  props: SortSectionQuestionsProps,
): Promise<FormSectionDto> => {
  const response =
    await formsSectionsConfigApi.formSectionsConfigControllerSortSectionQuestions(
      props.sectionId,
      props.dto,
    );

  return response.data;
};

export const useSortSectionQuestions = (
  user: UserProfileDto | null,
  formId: string,
): {
  sortSectionQuestions: UseMutateFunction<
    FormSectionDto,
    Error,
    SortSectionQuestionsProps,
    unknown
  >;
  sortSectionQuestionsAsync: (
    props: SortSectionQuestionsProps,
  ) => Promise<FormSectionDto>;
  sortSectionQuestionsEnabled: boolean;
  isSuccessSortSectionQuestions: boolean;
  isPendingSortSectionQuestions: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORM_SECTIONS_CONFIG, ACTION_SORT_SECTION_QUESTIONS],
    mutationFn: enabled ? sortSectionQuestions : undefined,
    onSuccess(data) {
      const queryKey = [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId];
      const currentData = queryClient.getQueryData<FormSectionDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        return currentData?.map((section) => {
          if (section.id === data.id) {
            return data;
          }

          return section;
        });
      });
    },
  });

  return {
    sortSectionQuestions: mutate,
    sortSectionQuestionsAsync: mutateAsync,
    sortSectionQuestionsEnabled: enabled,
    isSuccessSortSectionQuestions: isSuccess,
    isPendingSortSectionQuestions: isPending,
  };
};
